import { useEffect, useState } from 'react';

export default function Hero() {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  const cards = [
    {
      icon: 'ri-award-line',
      title: 'Skill Badge',
      description: 'Complete microcourses and earn instant recognition',
      gradient: 'from-[#304FFE] to-[#7C4DFF]',
      delay: 'delay-[400ms]',
    },
    {
      icon: 'ri-medal-line',
      title: 'Microcredential',
      description: 'Stack badges to prove deeper mastery',
      gradient: 'from-[#7C4DFF] to-[#FF6B9D]',
      delay: 'delay-[500ms]',
    },
    {
      icon: 'ri-trophy-line',
      title: 'Merit Pathway',
      description: 'Unlock scholarships through achievement',
      gradient: 'from-[#FF6B9D] to-[#FFA726]',
      delay: 'delay-[600ms]',
    },
  ];

  return (
    <section className="relative min-h-screen flex items-center overflow-hidden pt-20">
      {/* Animated gradient background */}
      <div className="absolute inset-0 bg-gradient-to-br from-[#304FFE]/10 via-transparent to-[#7C4DFF]/10">
        <div className="absolute inset-0 bg-gradient-to-b from-black/5 via-transparent to-transparent"></div>
      </div>

      {/* Floating decorative elements */}
      <div className="absolute top-1/4 right-[10%] w-64 h-64 bg-gradient-to-br from-[#304FFE]/20 to-[#7C4DFF]/20 rounded-full blur-3xl animate-pulse"></div>
      <div className="absolute bottom-1/4 left-[15%] w-48 h-48 bg-gradient-to-br from-[#7C4DFF]/15 to-[#304FFE]/15 rounded-full blur-3xl animate-pulse delay-1000"></div>

      <div className="relative max-w-7xl mx-auto px-6 lg:px-12 w-full">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left content */}
          <div
            className={`space-y-8 transition-all duration-1000 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
          >
            <div className="inline-block px-5 py-2 bg-white rounded-full shadow-sm">
              <span className="text-xs font-semibold text-[#7C4DFF] uppercase tracking-wider">
                Learn. Earn. Progress.
              </span>
            </div>

            <h1 className="text-6xl lg:text-7xl font-bold text-[#1A1F36] leading-tight">
              <span className="block">Learn.</span>
              <span className="block ml-4">Earn.</span>
              <span className="block ml-8">Progress.</span>
            </h1>

            <p className="text-2xl lg:text-3xl font-light text-gray-700">
              Your future starts with one micro skill.
            </p>

            <p className="text-lg text-gray-600 leading-relaxed max-w-lg">
              Build real skills in under an hour. Earn badges and microcredentials. Unlock Merit Pathways™ that turn your effort into opportunity.
            </p>

            <div className="flex flex-wrap gap-4 pt-4">
              <button className="group px-8 py-4 bg-gradient-to-r from-[#304FFE] to-[#7C4DFF] text-white font-medium rounded-full hover:shadow-2xl hover:scale-105 transition-all flex items-center gap-3 whitespace-nowrap cursor-pointer">
                Earn Your First Badge
                <span className="w-6 h-6 bg-white/20 rounded-full flex items-center justify-center group-hover:translate-x-1 transition-transform">
                  <i className="ri-arrow-right-line text-sm"></i>
                </span>
              </button>
              <button className="px-8 py-4 border-2 border-[#7C4DFF] text-[#7C4DFF] font-medium rounded-full hover:bg-[#7C4DFF] hover:text-white transition-all whitespace-nowrap cursor-pointer">
                Explore Microcourses
              </button>
            </div>
          </div>

          {/* Right floating cards */}
          <div className="relative hidden lg:block">
            <div className="relative w-full h-[600px]">
              {cards.map((card, index) => (
                <div
                  key={index}
                  className={`absolute w-64 h-72 bg-white rounded-3xl shadow-2xl p-6 transform transition-all duration-500 hover:scale-[1.03] hover:shadow-2xl hover:-translate-y-2 cursor-pointer ${
                    isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
                  } ${card.delay} ${
                    index === 0 ? 'top-0 right-20 -rotate-3 hover:rotate-0' :
                    index === 1 ? 'top-32 right-0 rotate-2 hover:rotate-0' :
                    'top-64 right-32 -rotate-2 hover:rotate-0'
                  }`}
                  style={{
                    boxShadow: '0 8px 30px rgba(0, 0, 0, 0.12), 0 2px 8px rgba(0, 0, 0, 0.08)'
                  }}
                >
                  <div className={`w-14 h-14 bg-gradient-to-br ${card.gradient} rounded-2xl flex items-center justify-center mb-3`}>
                    <i className={`${card.icon} text-2xl text-white`}></i>
                  </div>
                  <h3 className="text-[22px] font-bold text-[#1A1F36] mb-3 leading-tight">{card.title}</h3>
                  <p className="text-sm text-gray-600">{card.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
